import React from "react";
import { Link } from "react-router-dom";
import logo from "./assets/logo.png";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-slate-900 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="text-orange-500 text-xl font-bold">
            <img src={logo} alt="Logo" className="h-12 w-auto" />
          </Link>
          <Link
            to="/"
            className="text-white hover:text-orange-500 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Privacy Policy
        </h1>
        <p className="text-lg font-semibold text-gray-800 mb-2">
              Jindo Technologies, LLC
        </p>
        <div className="prose prose-lg text-gray-600 space-y-6">
          <p className="text-sm text-gray-500">
          Effective Date: April 25, 2026
          </p>

          <section className="mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Introduction
            </h2>
            <p>
              Welcome to Jindo Technologies, LLC ("Jindo," "we," "our," or
              "us"). We are committed to protecting your privacy and ensuring
              the security of your personal information. This Privacy Policy
              explains how we collect, use, share, and protect your information when you use the Jindo application and its features — including calendar and email integrations and transcription. By using Jindo, you agree to the practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Information We Collect
            </h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              2.1 Information You Provide
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
               <b>Account Information:</b> Name, email address, and password when you create an account.
              </li>
              <li>
                <b>Profile Data:</b> Any optional profile information you add, such as a photo or display name.
              </li>
              <li>
                <b>Usage Data:</b> How you interact with the app, features used, and session duration.
              </li>
              <li>
                <b>Device Information:</b> Device type, operating system, device identifiers, and IP address.
              </li>
              <li>
                <b>Transcription Content:</b> Text transcripts generated through Jindo's transcription feature. Jindo does not store audio recordings — we process audio solely to generate your transcript and do not retain it. 
              </li>
              <li>
                <b>Integration Data:</b> Calendar events and email metadata accessed through Google or Microsoft integrations, and, for Google accounts, the full names and email addresses of people in your Google Workspace directory and in your Google "Other contacts." This data is used only to power the features described in this policy.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">
              2.2 Automatically Collected Information
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Usage data and analytics</li>
              <li>Device information and IP addresses</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. How We Use Your Information
            </h2>
            <p className="mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, operate, and maintain the Jindo app and all its features.</li>
              <li>
                Create and manage your account and verify your identity.
              </li>
              <li>Process conversations to deliver transcription results to you.</li>
              <li>Sync calendar events and send emails on your behalf when you have connected a Google or Microsoft account.</li>
              <li>Send you important updates, security alerts, and support messages.</li>
              <li>Improve the app's performance and diagnose technical issues.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
            <p className="mt-4">We do not use your transcription content or any third-party integration data to train AI or machine learning models without your express written consent. <b>We never use data received from Google APIs, including directory and contacts data, to develop, improve, or train generalized AI or machine learning models.</b></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Information Sharing
            </h2>
            <p className="mb-4">
              We do not sell, trade, or rent your personal information. We may share your information only in these limited circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><b>Service Providers:</b> Trusted vendors who help us operate the app (e.g., cloud hosting), bound by confidentiality agreements.</li>
              <li><b>Legal Requirements:</b> When required by law, court order, or to protect the safety of our users.</li>
              <li><b>Business Transfers:</b> In connection with a merger, acquisition, or sale of assets. <b>Google user data will be transferred in such a transaction only after we obtain your prior explicit consent.</b></li>
              <li><b>With Your Consent:</b> Any other sharing only occurs with your explicit permission.</li>
            </ul>
            <p className="mt-4">
              <b>Data received from Google APIs is subject to the additional restrictions in Section 5.</b>
          </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Third-Party Integrations: Google & Microsoft
            </h2>
             <p className="mb-2"> After logging into Jindo, Jindo requests access only to:</p>
              <h4 className="text-xl font-bold text-gray-900 my-2">Google Integration</h4>
            <p>When you connect your Google account and grant permission, Jindo requests access to the following:</p>
             <ul className="list-disc pl-6 space-y-2 my-4">
              <li><b>Google Calendar:</b> To read and sync your calendar events within Jindo.</li>
              <li><b>Gmail:</b> To send emails on your behalf through Jindo's email features.</li>
              <li><b>Google Workspace Directory (read-only):</b> To retrieve the full names and email addresses of colleagues in your organization's directory.</li>
              <li><b>Google Other Contacts (read-only):</b> To retrieve the full names and email addresses of people you have interacted with but have not saved as contacts.</li>
             </ul>
             <p>We do not access any other Google account data. You may revoke Jindo's access at any time via your Google Account permissions page (myaccount.google.com/permissions). Your use of Google services is also governed by Google's Privacy Policy.</p>
             <p className="mt-4"><b>How we use Google directory and contacts data: </b> We use this data only to provide a user-facing feature within Jindo: displaying the full names of the attendees on your calendar events. Google Calendar often provides only an email address for an attendee, so Jindo looks up the matching name in your directory and Other contacts and shows it alongside the email address in your attendee list. We use this data only for this purpose. We do not use it for advertising, we do not sell it, and we do not use it for any purpose unrelated to this feature.</p>
             <p className="mt-4"><b>How we store it: </b>Google user data is encrypted in transit (TLS) and at rest [confirm]. [We store only the fields needed for these features, for as long as your account is connected. When you disconnect Google or close your account, we delete this data within [30] days.]</p>
             <p className="mt-4"><b>How we share it: </b>We do not share Google user data with third parties except (a) with service providers that process it on our behalf to operate Jindo, such as cloud hosting [and AI model providers], who are contractually prohibited from using it for any other purpose, including training; (b) as required by law; or (c) with your consent.</p>
             <p className="mt-4"><b>Human access: </b>Jindo personnel will not read your Google user data unless we have your affirmative agreement for specific data, it is necessary for security purposes such as investigating abuse, it is necessary to comply with applicable law, or the data has been aggregated and anonymized for internal operations.</p>
             <p className="mt-4"><b>Revoking access: </b>You may revoke Jindo's access at any time via your Google Account permissions page (myaccount.google.com/permissions) or by disconnecting Google in Jindo settings. Your use of Google services is also governed by Google's Privacy Policy.</p>
             <p className="mt-4"><b>Limited Use: </b>Jindo's use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.</p>
           <h4 className="text-xl font-bold text-gray-900 mt-4 my-2">Microsoft Integration</h4>
            <p>When you connect your Microsoft account, Jindo requests access only to:</p>
            <ul className="list-disc pl-6 space-y-2 my-4">
              <li><b>Outlook Calendar:</b> To read and sync your calendar events within Jindo.</li>
              <li><b>Outlook Mail:</b> To send emails on your behalf through Jindo's email features.</li>
            </ul>
            <p className="my-4">We do not access any other Microsoft account data. You may revoke Jindo's access at any time via your Microsoft Account privacy settings (account.microsoft.com/privacy). Your use of Microsoft services is also governed by Microsoft's Privacy Statement.</p>
            <p>Data retrieved through either integration is used solely to operate the features you have requested. We do not store your Google or Microsoft emails or calendar data beyond what is necessary to display and operate the relevant Jindo features.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Transcription Feature & Your Responsibilities
            </h2>
            <p className="mb-4">Jindo's transcription feature processes your conversations and meetings to generate text transcripts. We do not store audio recordings. Audio is processed transiently to produce your transcript and is not retained by Jindo thereafter. The transcript itself is stored in your account and belongs to you.</p>
            <p className="mb-2">By using this feature, you acknowledge and agree that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are solely responsible for obtaining all legally required consents from all participants before initiating any transcription of a conversation.</li>
              <li>Transcription laws vary by jurisdiction. Many U.S. states — including California — and many countries require the consent of all parties before a conversation may be recorded or transcribed. It is your responsibility to understand and comply with the laws applicable to you and any participants.</li>
              <li>Jindo does not obtain consent on your behalf and makes no representation that any feature of the service satisfies the consent requirements of any jurisdiction.</li>
              <li>Transcription outputs may contain errors or inaccuracies. You are solely responsible for reviewing and verifying any transcript before relying on it for any purpose.</li>
              <li>You must not use this feature to unlawfully intercept or transcribe any communication, or to harass, defame, or harm any individual.</li>
            </ul>
            <p className="mt-4">You retain ownership of the transcription content you create. We store it only to provide the service to you, and will delete it upon your request or account closure.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Data Retention
            </h2>
            <p>
              We retain your personal information for as long as your account is active. Transcription content is retained until you delete it or close your account. Audio is not retained by default. Where the Service offers an option to save audio, this occurs only at the explicit request of the user. Upon a verified deletion request, we will delete your data within 30 days, except where retention is required by law. Data received from Google APIs is deleted within [30] days after you disconnect your Google account or request deletion, except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Security
            </h2>
            <p>
             We use industry-standard security measures including encryption in transit (TLS) and secure credential storage. No method of transmission or storage is 100% secure. Please use a strong, unique password and contact us immediately if you suspect unauthorized access to your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Children's Privacy
            </h2>
            <p>
            Jindo is not intended for users under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal data, contact us immediately and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Changes to This Policy
            </h2>
            <p>
             We may update this policy from time to time. We will notify you of material changes through the app or via email and update the effective date above. Continued use of Jindo after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy or our data
              practices, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Jindo Technologies, LLC</strong>
              <br />
              Californa, USA
              <br />
              Email: contact@jindolabs.com
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-4 space-x-6">
            <Link
              to="/privacy"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/google-api-disclosure"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Google API Disclosure
            </Link>
            <Link
              to="/microsoft-api-disclosure"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Microsoft API Disclosure
            </Link>
          </div>
          <p className="text-gray-400">© 2026 Jindo Technologies, LLC</p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
